"use client";

import React, { createContext, useContext, useRef } from "react";
import { useLocalStorage } from "@/lib/hooks/use-local-storage";
import { STORAGE_KEYS } from "@/lib/constants";
import { startSandbox, stopSandbox } from "@/app/actions";

// Define types for MCP server
export interface KeyValuePair {
  key: string;
  value: string;
}

export type ServerStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface MCPServer {
  id: string;
  name: string;
  url: string;
  type: 'sse' | 'stdio';
  command?: string;
  args?: string[];
  env?: KeyValuePair[];
  headers?: KeyValuePair[];
  description?: string;
  status?: ServerStatus;
  errorMessage?: string;
  sandboxUrl?: string; // Store the sandbox URL directly on the server object
  sandboxStartTime?: number; // Timestamp when sandbox was started
}

// Type for processed MCP server config for API
export interface MCPServerApi {
  type: 'sse';
  url: string;
  headers?: KeyValuePair[];
}

interface MCPContextType {
  mcpServers: MCPServer[];
  setMcpServers: (servers: MCPServer[]) => void;
  selectedMcpServers: string[];
  setSelectedMcpServers: (serverIds: string[]) => void;
  mcpServersForApi: MCPServerApi[];
  startServer: (serverId: string) => Promise<boolean>;
  stopServer: (serverId: string) => Promise<boolean>;
  updateServerStatus: (serverId: string, status: ServerStatus, errorMessage?: string) => void;
  getActiveServersForApi: () => MCPServerApi[];
}

const MCPContext = createContext<MCPContextType | undefined>(undefined);

// Helper function to wait for server readiness
async function waitForServerReady(url: string, maxAttempts = 20, timeout = 3000) {
  console.log(`Checking server readiness at ${url}, will try ${maxAttempts} times`);
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (response.status === 200) {
        console.log(`Server ready at ${url} after ${i + 1} attempts`);
        return true;
      }
      console.log(`Server not ready yet (attempt ${i + 1}), status: ${response.status}`);
    } catch (error) {
      console.log(`Server connection failed (attempt ${i + 1}): ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    
    // Wait before next attempt with progressive backoff
    const waitTime = Math.min(1000 * (i + 1), 5000); // Start with 1s, increase each time, max 5s
    console.log(`Waiting ${waitTime}ms before next attempt`);
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  console.log(`Server failed to become ready after ${maxAttempts} attempts`);
  return false;
}

export function MCPProvider({ children }: { children: React.ReactNode }) {
  const [mcpServers, setMcpServers] = useLocalStorage<MCPServer[]>(
    STORAGE_KEYS.MCP_SERVERS, 
    []
  );
  
  const [selectedMcpServers, setSelectedMcpServers] = useLocalStorage<string[]>(
    STORAGE_KEYS.SELECTED_MCP_SERVERS, 
    []
  );
  
  // Create a ref to track active servers and avoid unnecessary re-renders
  const activeServersRef = useRef<Record<string, boolean>>({});

  // Helper to get a server by ID
  const getServerById = (serverId: string): MCPServer | undefined => {
    return mcpServers.find(server => server.id === serverId);
  };
  
  // Update server status
  const updateServerStatus = (serverId: string, status: ServerStatus, errorMessage?: string) => {
    setMcpServers(currentServers => 
      currentServers.map(server => 
        server.id === serverId 
          ? { ...server, status, errorMessage: errorMessage || undefined } 
          : server
      )
    );
  };
  
  // Update server with sandbox URL
  const updateServerSandboxUrl = (serverId: string, sandboxUrl: string) => {
    // Update in memory and force save to localStorage
    setMcpServers(currentServers => {
      const updatedServers = currentServers.map(server => 
        server.id === serverId 
          ? { ...server, sandboxUrl, status: 'connected' as ServerStatus, sandboxStartTime: Date.now() } 
          : server
      );
      
      // Return the updated servers to set in state and localStorage
      return updatedServers;
    });
  };
  
  // Get active servers formatted for API usage
  const getActiveServersForApi = (): MCPServerApi[] => {
    return selectedMcpServers
      .map(id => getServerById(id))
      .filter((server): server is MCPServer => !!server)  // Remove connection requirement
      .map(server => ({
        type: 'sse' as const,
        url: server.type === 'stdio' && server.sandboxUrl ? server.sandboxUrl : server.url,
        headers: server.headers
      }));
  };
  
  // Start a server
  const startServer = async (serverId: string): Promise<boolean> => {
    const server = getServerById(serverId);
    if (!server) return false;
    
    // Mark server as connecting
    updateServerStatus(serverId, 'connecting');
    
    try {
      // For SSE servers, test actual MCP connection instead of simple fetch
      if (server.type === 'sse') {
        try {
          let testClient;
          let usedTransport = '';
          
          // Check if this is a Smithery server
          if (server.url.includes('server.smithery.ai')) {
            // Use ai package with proper Smithery URL format
            try {
              const { experimental_createMCPClient } = await import('ai');
              const { StreamableHTTPClientTransport } = await import('@modelcontextprotocol/sdk/client/streamableHttp.js');
              
              // Parse the original URL to extract components
              const urlObj = new URL(server.url);
              const apiKey = urlObj.searchParams.get('api_key');
              
              if (!apiKey) {
                throw new Error('Smithery URL must include api_key parameter');
              }
              
              // Extract server path (e.g., "@upstash/context7-mcp" from "https://server.smithery.ai/@upstash/context7-mcp/mcp")
              const pathParts = urlObj.pathname.split('/').filter(Boolean);
              
              // Find the "/mcp" part and extract everything before it as the server name
              const mcpIndex = pathParts.indexOf('mcp');
              if (mcpIndex === -1) {
                throw new Error('Invalid Smithery URL: must end with /mcp');
              }
              
              const serverName = pathParts.slice(0, mcpIndex).join('/');
              
              if (!serverName) {
                throw new Error('Invalid Smithery URL: missing server name');
              }
              
              // Create proper Smithery URL format based on the documentation
              // Format: https://server.smithery.ai/{serverName}/mcp?config={encodedConfig}&api_key={apiKey}
              const baseUrl = `https://server.smithery.ai/${serverName}/mcp`;
              const config = {}; // Empty config for now - can be extended to parse from URL params or headers
              
              // Encode config as base64 JSON (Smithery format)
              const configEncoded = btoa(JSON.stringify(config));
              
              // Build final URL with proper parameters
              const smitheryUrl = new URL(baseUrl);
              smitheryUrl.searchParams.set('config', configEncoded);
              smitheryUrl.searchParams.set('api_key', apiKey);
              
              // Create transport and client using ai package
              const transport = new StreamableHTTPClientTransport(smitheryUrl);
              testClient = await experimental_createMCPClient({
                transport: transport as any // Cast to satisfy TypeScript
              });
              
              usedTransport = 'Smithery (proper format)';
              
              // Test tools
              const tools = await testClient.tools();
              
            } catch (smitheryError) {
              throw smitheryError;
            }
          } else {
            // Use ai package for regular SSE servers
            const { experimental_createMCPClient } = await import('ai');
            
            const transport = {
              type: 'sse' as const,
              url: server.url,
              headers: server.headers?.reduce((acc, header) => {
                if (header.key) acc[header.key] = header.value || '';
                return acc;
              }, {} as Record<string, string>)
            };
            
            testClient = await experimental_createMCPClient({ transport });
            usedTransport = 'SSE';
            
            // Test tools
            const tools = await testClient.tools();
          }
          
          // If we get here, the connection works
          await testClient.close();
          
          updateServerStatus(serverId, 'connected');
          activeServersRef.current[serverId] = true;
          
          // Auto-enable server for chat when successfully started
          if (!selectedMcpServers.includes(serverId)) {
            setSelectedMcpServers([...selectedMcpServers, serverId]);
          }
          
          return true;
        } catch (error) {
          // Extract meaningful error message with detailed debugging for 400 errors
          let errorMessage = 'Connection failed';
          if (error instanceof Error) {
            if (error.message.includes('400')) {
              errorMessage = `Bad Request (400) - The URL format may be incorrect or this isn't a valid MCP endpoint. Check if this is a Smithery URL that needs proper formatting.`;
            } else if (error.message.includes('502')) {
              errorMessage = 'Server returned 502 Bad Gateway - check URL and API key';
            } else if (error.message.includes('401')) {
              errorMessage = 'Authentication failed - check API key';
            } else if (error.message.includes('404')) {
              errorMessage = 'Server not found - check URL';
            } else if (error.message.includes('timeout')) {
              errorMessage = 'Connection timeout - server may be slow';
            } else {
              errorMessage = error.message;
            }
          }
          
          updateServerStatus(serverId, 'error', errorMessage);
          return false;
        }
      }
      
      // For stdio servers, start a sandbox
      if (server.type === 'stdio' && server.command && server.args?.length) {
        // Check if we already have a valid sandbox URL
        if (server.sandboxUrl) {
          try {
            const isReady = await waitForServerReady(server.sandboxUrl);
            if (isReady) {
              updateServerStatus(serverId, 'connected');
              activeServersRef.current[serverId] = true;
              
              // Auto-enable server for chat when successfully started
              if (!selectedMcpServers.includes(serverId)) {
                setSelectedMcpServers([...selectedMcpServers, serverId]);
              }
              
              return true;
            }
          } catch {
            // If sandbox check fails, we'll create a new one
          }
        }
        
        // Create a new sandbox
        const { url } = await startSandbox({
          id: serverId,
          command: server.command,
          args: server.args,
          env: server.env,
        });
        
        // Wait for the server to become ready
        const isReady = await waitForServerReady(url);
        
        if (isReady) {
          // Store the sandbox URL and update status - do this first!
          updateServerSandboxUrl(serverId, url);
          
          // Mark as active
          activeServersRef.current[serverId] = true;
          
          // Auto-enable server for chat when successfully started
          if (!selectedMcpServers.includes(serverId)) {
            setSelectedMcpServers([...selectedMcpServers, serverId]);
          }
          
          return true;
        } else {
          // Failed to start
          updateServerStatus(serverId, 'error', 'Server failed to start');
          
          // Clean up sandbox
          try {
            await stopSandbox(serverId);
          } catch (error) {
            console.error(`Failed to stop non-responsive sandbox ${serverId}:`, error);
          }
          
          return false;
        }
      }
      
      // If we get here, something is misconfigured
      updateServerStatus(serverId, 'error', 'Invalid server configuration');
      return false;
    } catch (error) {
      // Handle any unexpected errors
      console.error(`Error starting server ${serverId}:`, error);
      updateServerStatus(serverId, 'error', 
        `Error: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  };
  
  // Stop a server
  const stopServer = async (serverId: string): Promise<boolean> => {
    const server = getServerById(serverId);
    if (!server) return false;
    
    try {
      // For stdio servers with sandbox, stop the sandbox
      if (server.type === 'stdio' && server.sandboxUrl) {
        try {
          await stopSandbox(serverId);
          
          // Mark as not active
          delete activeServersRef.current[serverId];
        } catch (error) {
          console.error(`Error stopping sandbox for server ${serverId}:`, error);
          // Continue with server cleanup even if sandbox stop fails
        }
      }
      
      // Auto-disable server for chat when stopped
      if (selectedMcpServers.includes(serverId)) {
        setSelectedMcpServers(selectedMcpServers.filter(id => id !== serverId));
      }
      
      // Update server status and clear sandbox info
      setMcpServers(currentServers => 
        currentServers.map(server => 
          server.id === serverId 
            ? { ...server, status: 'disconnected' as ServerStatus, sandboxUrl: undefined, sandboxStartTime: undefined } 
            : server
        )
      );
      return true;
    } catch (error) {
      console.error(`Error stopping server ${serverId}:`, error);
      // Even if there are errors, update the server state to disconnected
      setMcpServers(currentServers => 
        currentServers.map(server => 
          server.id === serverId 
            ? { ...server, status: 'disconnected' as ServerStatus, sandboxUrl: undefined, sandboxStartTime: undefined } 
            : server
        )
      );
      return true; // Return true since we cleaned up the server state
    }
  };
  
  // Calculate mcpServersForApi based on current state
  const mcpServersForApi = getActiveServersForApi();

  return (
    <MCPContext.Provider 
      value={{ 
        mcpServers, 
        setMcpServers, 
        selectedMcpServers, 
        setSelectedMcpServers,
        mcpServersForApi,
        startServer,
        stopServer,
        updateServerStatus,
        getActiveServersForApi
      }}
    >
      {children}
    </MCPContext.Provider>
  );
}

export function useMCP() {
  const context = useContext(MCPContext);
  if (context === undefined) {
    throw new Error("useMCP must be used within an MCPProvider");
  }
  return context;
} 